import { Injectable, OnDestroy } from '@angular/core';
import {
	AsyncSubject,
	BehaviorSubject,
	catchError,
	distinctUntilChanged,
	filter,
	Observable,
	skip,
	switchMap,
	takeUntil,
} from 'rxjs';
import { ChannelStoreService } from 'src/lib/services/stores/gabby/channel-store/channel-store.service';
import { DirectoryStoreService } from 'src/lib/services/stores/gabby/directory-store/directory-store.service';
import { MessageStoreService } from 'src/lib/services/stores/gabby/message-store/message-store.service';
import { UserStoreService } from 'src/lib/services/stores/users/user/user-store.service';
import { WebsocketService } from 'src/lib/services/websocket/websocket.service';

@Injectable()
export abstract class AbstractGabbyStateMachineComponent implements OnDestroy {
	protected _unsubscribe$ = new AsyncSubject<null>();
	protected _initialized = false;

	protected _activeDirectoryId = new BehaviorSubject<string>(undefined);

	constructor(
		protected websocketService: WebsocketService,
		protected userStore: UserStoreService,
		protected directoryStore: DirectoryStoreService,
		protected channelStore: ChannelStoreService,
		protected messageStore: MessageStoreService,
	) {}

	get activeDirectoryId$(): Observable<string> {
		return this._activeDirectoryId.asObservable().pipe(
			filter((x) => x !== undefined),
			distinctUntilChanged(),
		);
	}

	protected _baseInitialize(): void {
		if (this._initialized) {
			throw new Error('GabbyStateMachineService is already initialized');
		}
		this._initialized = true;

		this.websocketService
			.registerPresence$()
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe();

		this.websocketService.isConnected$
			.pipe(
				filter((connected) => connected),
				skip(1),
				switchMap(() => this.refreshState()),
				catchError((err) => {
					console.error('An error occurred while refreshing Gabby data.', err);
					return null;
				}),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();
	}

	protected abstract refreshState(): Promise<unknown>;

	ngOnDestroy() {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
